/**
 * General Ledger View
 *
 * Comprehensive fund accounting interface with:
 * - Ledger entry viewing and filtering
 * - Trial balance reporting
 * - Chart of accounts management
 * - Financial statement generation
 * - Audit trail and compliance reporting
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '../ui/button';
import { Tabs, TabsContent } from '../ui/tabs';

import type { Fund } from '../../types';
import type { GeneralLedger } from '../../types/drawdown';

import {
  CreateAdjustmentModal,
  JournalEntriesTab,
  BankEntriesTab,
  TrialBalanceTab,
  FinancialStatementsTab,
  JournalEntryDetailModal,
} from '.';
import { CapitalAccountsTab } from '../capital-accounts';
import { IncomingTransactionsView } from '../bank-incoming';
import { Calculator } from 'lucide-react';

interface GeneralLedgerViewProps {
  fundId?: string;
  fund?: Fund;
  className?: string;
}

export function GeneralLedgerView({
  fundId,
  fund,
  className,
}: GeneralLedgerViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<GeneralLedger | null>(
    null
  );
  const activeTab = searchParams.get('section') || 'entries';

  const setActiveTab = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('section', value);
      return newParams;
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        {activeTab === 'entries' && (
          <div className="flex justify-end print:hidden">
            <Button
              onClick={() => setShowAdjustmentModal(true)}
              size="sm"
              className="flex items-center gap-2"
              disabled={!fund}
            >
              <Calculator className="h-4 w-4" />
              Create Adjustment
            </Button>
          </div>
        )}

        <TabsContent value="entries">
          <JournalEntriesTab fundId={fundId} onSelectEntry={setSelectedEntry} />
        </TabsContent>

        <TabsContent value="bank-entries">
          <BankEntriesTab fundId={fundId} />
        </TabsContent>

        <TabsContent value="incoming-transactions">
          {fundId && (
            <IncomingTransactionsView
              key={fundId}
              fundId={fundId}
              showHeading={false}
            />
          )}
        </TabsContent>

        <TabsContent value="trial-balance">
          {fundId && <TrialBalanceTab fundId={fundId} />}
        </TabsContent>

        <TabsContent value="capital-accounts">
          {fundId && <CapitalAccountsTab fundId={fundId} />}
        </TabsContent>

        <TabsContent value="statements">
          {fundId && <FinancialStatementsTab fundId={fundId} />}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {fund && (
        <CreateAdjustmentModal
          key={showAdjustmentModal ? 'open' : 'closed'}
          open={showAdjustmentModal}
          onClose={() => setShowAdjustmentModal(false)}
          onSuccess={() => setShowAdjustmentModal(false)}
          fund={fund}
        />
      )}

      <JournalEntryDetailModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </div>
  );
}
